import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';

import { LegacyChartTypes, MapTypes, TTLineChartTypes } from '@23degrees/shared-code/config/constants';
import { FONT_CONFIG } from '@23degrees/shared-code/config/font';
import { getStyleObjects } from '@23degrees/shared-code/content/content-utilities';
import { updateInlineValueStyle } from '@23degrees/shared-code/content/style';
import type { Content, FontFormat, MultiMapLayer, SymbolLayer } from '@23degrees/shared-code/models';
import { deepFilter, isObject } from '@23degrees/shared-code/utilities';

import FontFaceObserver from 'fontfaceobserver';
import get from 'lodash/get';
import set from 'lodash/set';

export interface FontType {
  fontFamily: string;
  fontPath: string;
  fontTypes: string[];
  fontFaces: FontFace[];
}

export interface FontFace {
  style: FontStyle;
  weight: FontWeight;
  file: string;
  local: string[];
}

export type FontStyle = 'normal' | 'italic';
export type FontWeight = '100' | '200' | '300' | '400' | '500' | '600' | '700' | '800' | '900';

const quillFontFn = (value: any) => isObject(value) && ('font' in value || 'weight' in value);

const getFontsOfDeltaContent = (deltaString: string, parentFontFormat: FontFormat, source?: string): any[] => {
  try {
    if (!deltaString || deltaString.length === 0) return [];

    const deltaFonts = [];
    const deltaContent = JSON.parse(deltaString);
    const deltaStyles = deepFilter<{ font?: string; weight?: string }>(deltaContent.ops, quillFontFn);

    for (const style of deltaStyles) {
      const fontObj = {
        fontFamily: style.font ?? parentFontFormat.fontFamily,
        weight: style.weight ?? parentFontFormat.weight,
      };

      deltaFonts.push(fontObj);
    }

    return deltaFonts;
  } catch (e) {
    // console.error('quilly quilly quilly', error)
    return [];
  }
};

@Injectable({
  providedIn: 'root',
})
export class FontService {
  private document = inject<Document>(DOCUMENT);

  private loadedFonts = [];

  private blackList = ['serif', 'sans-serif', 'handwriting', 'monospace', 'display'];

  private ext2format = {
    eot: 'embedded-opentype',
    otf: 'opentype',
    ttf: 'truetype',
    woff: 'woff',
    woff2: 'woff2',
  };

  private getFormat = (url) => {
    const ext = url.substr(url.lastIndexOf('.') + 1);
    return this.ext2format[ext];
  };

  private createFontFace = (font: FontType, fontFace: FontFace) => {
    const sources = fontFace.local.map((obj) => `local('${obj}')`);
    for (const type of font.fontTypes) {
      sources.push(`url('${font.fontPath + fontFace.file}.${type}') format('${this.ext2format[type]}')`);
    }

    return `@font-face {
  font-family: '${font.fontFamily}';
  font-style: ${fontFace.style};
  font-weight: ${fontFace.weight};
  font-display: swap;
  src: ${sources.join(', ')}
}`;
  };

  public createFontStyle = async (fontFamily: string, weight: string, fontStyle = 'normal') => {
    if (!fontFamily || !weight || !fontStyle) return;
    if (typeof fontFamily !== 'string' || typeof weight !== 'string' || typeof fontStyle !== 'string') return;

    const fonts = fontFamily.split(',').map((obj) => obj.trim().replace(/["']/g, ''));
    for (const font of fonts) {
      try {
        const loadedKey = `${font}-${weight}-${fontStyle}`;
        // console.log(
        //   font,
        //   weight,
        //   fontStyle,
        //   'blacklist:',
        //   this.blackList.includes(font),
        // );

        if (this.blackList.includes(font)) continue;

        // console.log(
        //   font,
        //   weight,
        //   fontStyle,
        //   'available:',
        //   FONT_CONFIG.some((obj) => obj.fontFamily === font),
        //   'loaded:',
        //   this.loadedFonts.includes(loadedKey),
        // );

        if (this.loadedFonts.includes(loadedKey)) continue;

        const fontConfig = FONT_CONFIG.find((obj) => obj.fontFamily === font) as FontType;
        if (!fontConfig) {
          // console.log('[FontService]', 'fontConfig not found', font);
          continue
        }

        const fontFace = fontConfig.fontFaces.find((obj) => obj.weight === weight && obj.style === fontStyle);
        if (!fontFace) {
          // console.log('[FontService]', 'fontFace not found', font, weight, fontStyle);
          continue;
        }

        const cssFontFace = this.createFontFace(fontConfig, fontFace);
        await this.attachFont(fontConfig, fontFace, cssFontFace);

        this.loadedFonts.push(loadedKey);
      } catch (error) {
        console.error('[FontService]', 'font loading failed', font, error);
      }
    }

    // console.log('[FontService]', 'font loading finished', this.loadedFonts);
  };

  private loadFont = async (fontType: FontType, fontFace: FontFace) => {
    const fontObs = new FontFaceObserver(fontType.fontFamily, { weight: fontFace.weight, style: fontFace.style });
    const font = await fontObs.load();
    // console.log('[FontService]', 'loaded', fontType.fontFamily, fontFace.weight, fontFace.style);

    return font;
  };

  public attachFont = async (fontType: FontType, fontFace: FontFace, fontStyle: string) => {
    const node = this.document.createElement('style');

    node.innerHTML = fontStyle;
    this.document.body.appendChild(node);

    return this.loadFont(fontType, fontFace);
  };

  public loadContentFonts = async (content) => {
    const styleObjects = [...getStyleObjects(content), ...this.getQuillFonts(content)];

    const uniqueStyleKeys = [];
    const uniqueStyleObjects = [];

    for (const obj of styleObjects) {
      obj.fontFamily = obj.fontFamily ?? obj.font; // BAD
      const key = `${obj.fontFamily}-${obj.weight}-${obj.fontStyle || 'normal'}`;

      if (!uniqueStyleKeys.includes(key)) {
        uniqueStyleObjects.push(obj);
        uniqueStyleKeys.push(key);
      }
    }

    const fontPromises: Promise<void>[] = [];
    for (const styleObject of uniqueStyleObjects) {
      const fontPromise = this.createFontStyle(styleObject.fontFamily, styleObject.weight, styleObject.fontStyle);
      fontPromises.push(fontPromise);
    }

    // wait for all fonts to be loaded, should run parallel
    await Promise.allSettled(fontPromises);

    return this.loadedFonts;
  };

  public getQuillFonts = (content: Content) => {
    if (!content.type || !content.typeSpecific) return [];

    const { advancedTooltip, hoverTooltip, chart, charts23, data, map, style } = content.typeSpecific;

    let fonts = [
      ...getFontsOfDeltaContent(content.name, style?.title, 'name'),
      ...getFontsOfDeltaContent(content.subTitle, style?.subTitle, 'subTitle'),
      ...getFontsOfDeltaContent(content.description, style?.description, 'description'),
      ...getFontsOfDeltaContent(data?.publisher?.name, style?.source?.font, 'publisher'),
      ...getFontsOfDeltaContent(advancedTooltip?.content, advancedTooltip?.style?.font, 'advancedTooltip'),
      ...getFontsOfDeltaContent(hoverTooltip?.content, hoverTooltip?.style?.font, 'hoverTooltip'),
    ];

    if (LegacyChartTypes.includes(content.type)) {
      fonts.push(...getFontsOfDeltaContent(chart?.axis?.labelAxis?.quillValue, chart?.axis?.labelAxis.valueStyle, 'labelAxis'));
      fonts.push(...getFontsOfDeltaContent(chart?.values?.quillValue, chart?.values?.style, 'values'));
      fonts.push(...getFontsOfDeltaContent(chart?.donut?.quillValue, chart?.donut?.quillStyle, 'donut'));
    }

    if (charts23) {
      // Juan Linechart
      if (TTLineChartTypes.includes(content.type)) {
        const { lineChart } = charts23;
        if (lineChart) {
          const { annotations, axis, labels, legend, ranges } = lineChart;
          const [xAxis, yAxis] = axis;

          if (annotations) {
            for (const annotation of annotations.values) {
              fonts.push(...getFontsOfDeltaContent(annotation.quill.quill, annotations.defaults.styles.quill.valueStyle, 'lineChart-annotations'));
            }
          }

          if (labels) {
            fonts.push(...getFontsOfDeltaContent(labels.labelsQuill.quill, labels.labelsQuill.valueStyle, 'lineChart-labels'));
            for (const customLabel of labels.customQuills) {
              fonts.push(...getFontsOfDeltaContent(customLabel.quill, labels.labelsQuill.valueStyle, 'lineChart-labels'));
            }
          }

          if (legend) {
            fonts.push(...getFontsOfDeltaContent(legend.labelsQuill.quill, legend.labelsQuill.valueStyle, 'lineChart-legend'));
            for (const customLegend of legend.customQuills) {
              fonts.push(...getFontsOfDeltaContent(customLegend.quill, legend.labelsQuill.valueStyle, 'lineChart-legend'));
            }
          }

          if (ranges) {
            for (const range of ranges.values) {
              fonts.push(...getFontsOfDeltaContent(range.quill.quill, ranges.defaults.styles.quill.valueStyle, 'lineChart-ranges'));
            }
          }

          if (xAxis) {
            fonts.push(...getFontsOfDeltaContent(xAxis.title.quill, xAxis.title.valueStyle, 'lineChart-xAxisTitle'));
            fonts.push(...getFontsOfDeltaContent(xAxis.labelsQuill.quill, xAxis.labelsQuill.valueStyle, 'lineChart-xAxisTitle'));
          }

          fonts.push(...getFontsOfDeltaContent(yAxis.title.quill, yAxis.title.valueStyle, 'lineChart-yAxisTitle'));
          fonts.push(...getFontsOfDeltaContent(yAxis.labelsQuill.quill, yAxis.labelsQuill.valueStyle, 'lineChart-yAxisTitle'));
        }
      }
    }

    if (MapTypes.includes(content.type)) {
      fonts.push(...getFontsOfDeltaContent(map?.annotations?.custom?.tooltip?.content, map?.annotations?.custom?.tooltip.style?.font, 'annotations'));

      if (map?.annotations?.custom?.markers?.features?.length > 0) {
        let markerTooltipFonts = [];
        for (const feature of map.annotations.custom.markers.features) {
          markerTooltipFonts = [
            ...markerTooltipFonts,
            ...getFontsOfDeltaContent(feature.properties?.tooltip?.value, feature.properties?.text?.style?.font),
            ...getFontsOfDeltaContent(feature.properties?.text?.value, feature.properties?.text?.style?.font),
          ];
        }

        fonts = [...fonts, ...markerTooltipFonts];
      }

      if (map?.layers?.length > 0) {
        const symbolLayers: MultiMapLayer<SymbolLayer>[] = map.layers.filter((layer) => layer.type === 'symbol') || [];
        const layerFonts = [].concat(...symbolLayers.map((layer) => getFontsOfDeltaContent(layer.settings.text.content, layer.settings.text.font)));

        fonts = [...fonts, ...layerFonts];
      }

      if(map?.miniMap?.text){
        fonts = [...fonts, ...getFontsOfDeltaContent(map?.miniMap?.text.content, map?.miniMap?.text.font)]
      }
    }

    return fonts;
  };

  public quillUpdate = (content: Content, key: string, fontStyle: FontFormat) => {
    try {
      const quillStr = updateInlineValueStyle(get(content, key), <any>fontStyle, <any>content.typeSpecific.fields);
      set(content, key, quillStr);
    } catch (error) {
      console.error('[FontService]', 'quillUpdate', error);
    }
  };
}
